import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { IconRowInsertBottom, IconTrash, IconX } from '@rossum/ui/icons/tabler';
import {
  Box,
  IconButton,
  Slide,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { disableDatapointsActionsSelector } from '../../redux/modules/datapoints/selector';
import { complexLineItemsEnabledSelector } from '../../redux/modules/ui/selectors';
import { FooterLayout } from '../../types/user';
import ItemWrapper from './ItemWrapper';

const SELECTION_TEXT_MIN_WIDTH = 110;
const TOOLTIP_DELAY = 600;

type FooterSelectionPanelProps = {
  itemsCount: number;
  onDeleteSelected: () => void;
  onClearSelected: () => void;
  onAddTuple: () => void;
  footerLayout: FooterLayout;
};

export const FooterSelectionPanel = ({
  itemsCount,
  onDeleteSelected,
  onClearSelected,
  onAddTuple,
  footerLayout,
}: FooterSelectionPanelProps) => {
  const intl = useIntl();
  const showSelectionPanel = itemsCount > 0;
  const complexLineItemsEnabled = useSelector(complexLineItemsEnabledSelector);

  const disableActions = useSelector(disableDatapointsActionsSelector);

  return (
    <Slide direction="right" in={showSelectionPanel} mountOnEnter unmountOnExit>
      <Stack
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        height="100%"
        sx={{
          position: 'absolute',
          top: theme => theme.spacing(1.5),
          left: theme =>
            footerLayout === 'one-table'
              ? theme.spacing(10.5)
              : theme.spacing(14),
          pl: 2,
          pr: 1,
          minHeight: theme => theme.spacing(6.75),
          backgroundColor: theme => theme.palette.primary.main,
          color: theme => theme.palette.common.white,
          borderRadius: borderRadius('m'),
          zIndex: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{ display: 'inline-block', minWidth: SELECTION_TEXT_MIN_WIDTH }}
        >
          {intl.formatMessage(
            {
              id: 'components.documentValidation.footer.selectionPanel.selected',
            },
            {
              count: itemsCount,
              emphasize: (msg: ReactNode) => (
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {msg}
                </Box>
              ),
            }
          )}
        </Typography>
        {showSelectionPanel && (
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            height="100%"
            flexGrow={1}
          >
            <ItemWrapper
              icon={
                <SvgIcon fontSize="large">
                  <IconTrash />
                </SvgIcon>
              }
              label={intl.formatMessage({
                id: 'components.documentValidation.footer.selectionPanel.delete',
              })}
              onClick={() => onDeleteSelected()}
              disabled={disableActions}
            />
            {complexLineItemsEnabled ? (
              <ItemWrapper
                icon={
                  <SvgIcon fontSize="large">
                    <IconRowInsertBottom />
                  </SvgIcon>
                }
                label={intl.formatMessage({
                  id: 'components.documentValidation.footer.selectionPanel.insertLine',
                })}
                onClick={onAddTuple}
                disabled={disableActions}
              />
            ) : null}
          </Stack>
        )}
        <IconButton
          color="inherit"
          onClick={() => onClearSelected()}
          sx={{
            '& #cancel-selection-clear-icon': {
              opacity: 0.6,
              transition: 'opacity 0.2s',
            },
            '&:hover #cancel-selection-clear-icon': {
              opacity: 1,
            },
          }}
        >
          <Tooltip
            enterDelay={TOOLTIP_DELAY}
            title={intl.formatMessage({
              id: 'components.documentValidation.footer.selectionPanel.buttons.deselect',
            })}
          >
            <SvgIcon
              fontSize="large"
              id="cancel-selection-clear-icon"
              data-cy="cancel-selection-clear-icon"
            >
              <IconX />
            </SvgIcon>
          </Tooltip>
        </IconButton>
      </Stack>
    </Slide>
  );
};
